/**
 * Standard Actions configuration for Dawn.
 *
 * Storefront Renderer injects the Shopify Standard Actions bundle
 * (`window.Shopify.actions.{updateCart,openCart,getCart,…}`). This file
 * overrides the bundle's built-in Dawn refresh path with an explicit,
 * in-theme version so forks that change Dawn's cart contract keep
 * working. Remove this file and the built-in defaults take over.
 *
 *   - openCart   — opens <cart-drawer>; falls back to /cart.
 *   - updateCart — after the Storefront API mutation, refreshes the
 *     affected cart sections and publishes `cart-update` so Dawn's
 *     pubsub subscribers react.
 *   - other actions (getCart, etc.) keep the default implementation.
 */

// Cart custom elements that advertise sections via getSectionsToRender().
// If Dawn adds a new cart custom element, add its tag here.
const DAWN_CART_TAGS = ['cart-drawer', 'cart-items', 'cart-drawer-items', 'cart-notification'];

// Sections that Dawn's own pubsub subscribers refresh (cart-items.js's
// CartItems#onCartUpdate fetches and replaces these directly when
// cart-update fires). We skip them here to avoid double-rendering.
// The drawer body ('cart-drawer:cart-drawer') is not skipped: the
// subscribers only swap items and footer, so an empty drawer would
// keep its empty-state markup after an external add.
// Format is '<element-tag>:<getSectionsToRender entry id>'.
// If you change which sections those subscribers refresh, update this set.
const DAWN_PUBSUB_REFRESHED_SECTIONS = new Set([
  'cart-drawer-items:CartDrawer',
  'cart-items:main-cart-items',
]);

// Walk every mounted Dawn cart custom element, collect the sections it
// wants rendered, and dedupe. Returns a Map keyed by section id, each
// entry pointing at the DOM mount and the selector used to extract the
// fresh fragment.
function collectCartSections() {
  const sections = new Map();

  for (const el of document.querySelectorAll(DAWN_CART_TAGS.join(','))) {
    let entries;
    try {
      entries = el.getSectionsToRender?.();
    } catch {
      continue;
    }

    const tag = el.tagName.toLowerCase();
    for (const entry of entries ?? []) {
      if (DAWN_PUBSUB_REFRESHED_SECTIONS.has(`${tag}:${entry.id}`)) continue;

      const sectionId = entry.section ?? entry.id;
      if (!sectionId || sections.has(sectionId)) continue;

      // Two patterns coexist in getSectionsToRender():
      //   - cart-items style: entry.section is the parent Liquid section
      //     id, entry.selector is a child node inside it.
      //   - cart-drawer / cart-notification style: entry.id IS the mount;
      //     there is no parent wrapper.
      const root = entry.section ? document.getElementById(entry.id) : document;
      if (!root) continue;

      const mount = entry.selector
        ? (root.querySelector(entry.selector) ?? (entry.section ? root : null))
        : document.getElementById(entry.id);
      if (!mount) continue;

      sections.set(sectionId, {
        mount,
        extractSelector: entry.selector || '.shopify-section',
      });
    }
  }

  return sections;
}

// After a Storefront API mutation, refresh every Dawn cart section
// that isn't already refreshed by Dawn's own pubsub subscribers, then
// publish 'cart-update' so the subscribers run.
//
// We always fetch /cart.js so that `cartData` is defined for subscribers.
// quick-add-bulk.js reads `event.cartData.items` unconditionally —
// publishing without cartData makes it throw. Sections come from
// /cart?sections= because GET /cart.js ignores the sections parameter.
async function refreshDawnCartUI() {
  const sections = collectCartSections();
  // `routes` is a Dawn global, but don't assume it's defined.
  const cartUrl = (typeof routes !== 'undefined' && routes?.cart_url) || '/cart';
  // No Accept: application/json header here: with it, /cart?sections=
  // answers with the cart payload instead of the rendered sections.
  const getJSON = (url) =>
    fetch(url)
      .then((r) => (r.ok ? r.json() : null))
      .catch(() => null);

  const [cartData, sectionsData] = await Promise.all([
    getJSON(`${cartUrl}.js`),
    sections.size ? getJSON(`${cartUrl}?sections=${[...sections.keys()].join(',')}`) : null,
  ]);

  if (sectionsData) {
    for (const [id, { mount, extractSelector }] of sections) {
      const html = sectionsData[id];
      if (!html) continue;
      const source = new DOMParser()
        .parseFromString(html, 'text/html')
        .querySelector(extractSelector);
      if (source) mount.replaceChildren(...source.childNodes);
    }

    // The overlay lives inside the re-rendered drawer body; rebind it
    // the same way CartDrawer#renderContents does.
    const drawer = document.querySelector('cart-drawer');
    drawer?.querySelector('#CartDrawer-Overlay')?.addEventListener('click', drawer.close.bind(drawer));
  }

  // Mirror CartItems#updateQuantity: the empty-state class lives on
  // wrappers that the pubsub subscribers don't re-render.
  if (cartData) {
    const isEmpty = cartData.item_count === 0;
    for (const el of document.querySelectorAll('cart-drawer, cart-items, #main-cart-footer')) {
      el.classList.toggle('is-empty', isEmpty);
    }
  }

  // Hand off to Dawn's existing subscribers. cartData is the full
  // Cart Ajax payload (items, item_count, token, …); quick-add-bulk.js
  // and price-per-item.js read it directly.
  publish(PUB_SUB_EVENTS.cartUpdate, {
    source: 'external-refresh',
    cartData: cartData ?? undefined,
  });
}

// Shopify Inbox's <shopify-chat> is hidden by CSS while the drawer is
// open, so an open chat panel comes back as it was once the cart closes.
// On the cart page the refreshed page already shows the cart, so the
// drawer stays closed.
function showCartDrawer() {
  const onCartPage = !!document.getElementById('main-cart-items');
  const drawer = document.querySelector('cart-drawer');
  if (!onCartPage && (!drawer || typeof drawer.open !== 'function')) return false;
  if (!onCartPage) drawer.open();
  return true;
}

// Adds carry a merchandiseId without a line id; updates and removals
// reference an existing line.
const addsLines = (input) => input?.lines?.some((line) => line.merchandiseId && !line.id && line.quantity > 0);

function initStandardActions() {
  const actions = window.Shopify?.actions;
  if (!actions) return;

  actions.openCart.configure({
    async handler(defaultHandler) {
      if (showCartDrawer()) return;
      return defaultHandler();
    },
  });

  actions.updateCart.configure({
    // Dawn doesn't currently listen for shopify:cart:* events, but the
    // bundle requires an eventTarget. document is the conventional root.
    eventTarget: () => document,
    async handler(defaultHandler, input) {
      const result = await defaultHandler();
      try {
        await refreshDawnCartUI();
        // Match Dawn's product form: open the drawer after an add so
        // external callers (e.g. Shopify Inbox) show the updated cart.
        if (addsLines(input) && !result?.userErrors?.length) {
          showCartDrawer();
        }
      } catch (error) {
        console.error('[Dawn] Standard Actions cart refresh failed; reloading.', error);
        window.location.reload();
      }
      return result;
    },
  });
}

// Run immediately if the standard-actions bundle has already attached
// `Shopify.actions`; otherwise wait for DOMContentLoaded, which fires after
// all module scripts have executed regardless of document order.
if (window.Shopify?.actions) {
  initStandardActions();
} else {
  document.addEventListener('DOMContentLoaded', initStandardActions, { once: true });
}
